import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { useCallback, useMemo } from "react";
import { PlusIcon } from "@icons";
import { useUser } from "@hooks/useUser";
import { Button } from "@design-system/Button/Button";
import { CaptureClicks } from "@design-system/CaptureClicks/CaptureClicks";
import { TeamList } from "@components/organizationView/clients/TeamList/TeamList";
import { useUnaffiliatedMembers } from "@components/organizationView/clients/useUnaffiliatedMembers";
import { useOverlays } from "contexts/OverlayContext";
import {
    USER_WORKSPACE_ROLE_ACTIONS,
    WorkspaceAction,
} from "@common/userWorkspaceRoles";
import styles from "./WorkspaceView.module.scss";
import { WorkspaceDetailsRow } from "./workspaceViewConfig";

export const MembersCell = ({
    row,
}: CellContext<ViewTableFeatures, WorkspaceDetailsRow, unknown>) => {
    const { openOverlay } = useOverlays();
    const user = useUser();
    const actingUserAssignment = row.original.workspace.userAssignments?.find(
        (assignment) => assignment.userId === user.id,
    );

    const actingUserRoleInWorkspace = actingUserAssignment?.role;

    const canAddMembers =
        actingUserRoleInWorkspace !== undefined &&
        USER_WORKSPACE_ROLE_ACTIONS[actingUserRoleInWorkspace].includes(
            WorkspaceAction.MEMBERS_EDIT,
        );

    const members = row.original.assignments;
    const invites = row.original.pendingInvites;

    const nonTeamMembers = useUnaffiliatedMembers(members);

    const workspace = row.original.workspace;
    const addMemberOverlayPayload = useMemo(
        () => ({
            workspaceId: workspace.id,
            entities: workspace.entities,
            actingUserRole: actingUserRoleInWorkspace,
            addMemberHeader: `Add member to ${workspace.name}`,
        }),
        [
            actingUserRoleInWorkspace,
            workspace.entities,
            workspace.id,
            workspace.name,
        ],
    );
    const handleOpenAddMember = useCallback(
        () => openOverlay("memberManagementAddMember", addMemberOverlayPayload),
        [addMemberOverlayPayload, openOverlay],
    );

    return (
        <CaptureClicks>
            <TeamList
                team={nonTeamMembers}
                invites={invites}
                appendContent={
                    canAddMembers ? (
                        <Button
                            variant="primary"
                            iconOnly
                            size="sm"
                            aria-label="Add member"
                            className={styles.addMember}
                            onClick={handleOpenAddMember}
                        >
                            <PlusIcon />
                        </Button>
                    ) : null
                }
                iconSize="sm"
            />
        </CaptureClicks>
    );
};
